import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { LOCATION_DATA } from 'utils/constants';
import { isLocationPls } from 'utils/locations';
import { FREE_PLAY_SAPPHIRE_COST } from '../SapphireTracker';
import FreePlayIcon from '../assets/FreePlayWithSparkles.png';
import SapphireIcon from '../assets/SapphireWithSparkles.png';
import {
  getCmpFreePlayRewardValue,
  getPlsFreePlayRewardValue
} from './getFreePlayRewardValue';
import './PromoSection.css';

const WSTORE_LINK_TO = {
  pathname: '/wstore',
  state: { categoryValue: 'P - Free Play', initiator: 'vault-promo-btn' }
};

const GAMES_LINK_TO = {
  pathname: '/slots',
  state: { initiator: 'vault-promo-btn' }
};

const PromoSection = ({
  remainingSapphiresToEarn,
  totalSapphiresToEarn,
  totalSapphiresEarned,
  casinoLocation,
  patronRewards,
  plsRewards
}) => {
  const earnedSapphireRatio = totalSapphiresEarned / totalSapphiresToEarn;
  const propertyIcon = LOCATION_DATA[casinoLocation].image;

  const usersFreePlayRewardValue = isLocationPls(casinoLocation)
    ? getPlsFreePlayRewardValue(plsRewards)
    : getCmpFreePlayRewardValue(patronRewards);

  const openBookingPage = () => {
    const { bookingUrl, url } = LOCATION_DATA[casinoLocation];

    window.open(bookingUrl ?? url);
  };

  const PromoIcon = ({ src, alt, className = '' }) => (
    <img className={`promo-icon ${className}`} src={src} alt={alt} />
  );

  const Title = ({ text, isSmallTitle, className = '' }) => (
    <div className={`${isSmallTitle ? 'small-title' : 'title'} ${className}`}>
      {text}
    </div>
  );

  const SupportingText = ({ children, className = '' }) => (
    <div className={`supporting-text ${className}`}>{children}</div>
  );

  const ActionButton = ({ onClick, text, className = '', to }) => {
    if (to) {
      return (
        <Link className={className ? className : 'outlined-btn'} to={to}>
          {text}
        </Link>
      );
    }
    return (
      <button
        type="button"
        className={`outlined-btn ${className}`}
        onClick={onClick}
      >
        {text}
      </button>
    );
  };

  const renderContent = () => {
    if (totalSapphiresToEarn < FREE_PLAY_SAPPHIRE_COST) {
      if (earnedSapphireRatio < 1) {
        return (
          <>
            <PromoIcon src={SapphireIcon} alt="" />
            <Title
              text="Don't let the fun pass you by!"
              className="larger-width"
            />
            <ActionButton
              text="Plan Your Visit"
              onClick={openBookingPage}
              className="down"
            />
          </>
        );
      } else {
        return (
          <>
            <Title
              text="You've discovered all of your Sapphires!"
              isSmallTitle={true}
            />
            <PromoIcon src={SapphireIcon} alt="" />
            <Title text="What's next?" />
            <SupportingText className="small">
              Visit the WStore to redeem offers, then use them on your next
              visit to a Wind Creek Property!
            </SupportingText>
            <ActionButton text="Visit WStore" to={WSTORE_LINK_TO} />
          </>
        );
      }
    } else {
      if (totalSapphiresEarned < FREE_PLAY_SAPPHIRE_COST) {
        return (
          <>
            <PromoIcon src={FreePlayIcon} alt="" className="free-play-small" />
            <Title
              text="Don't let the fun pass you by!"
              className="larger-width"
            />
            <SupportingText>
              Keep playing online games to earn Sapphires for great rewards like
              FREE Play!
            </SupportingText>
            <ActionButton text="Explore Games" to={GAMES_LINK_TO} />
          </>
        );
      } else if (remainingSapphiresToEarn === 0 && usersFreePlayRewardValue) {
        return (
          <>
            <Title
              text="You've discovered all of your Sapphires!"
              isSmallTitle={true}
            />
            <PromoIcon src={SapphireIcon} alt="" />
            <SupportingText className="location-based">
              Visit a Wind Creek property and use your
              <strong> {usersFreePlayRewardValue} in FREE Play!</strong>
            </SupportingText>
            <SupportingText>Come visit us</SupportingText>
            <ActionButton text="Plan Your Visit" onClick={openBookingPage} />
          </>
        );
      } else if (
        totalSapphiresEarned >= FREE_PLAY_SAPPHIRE_COST &&
        !usersFreePlayRewardValue
      ) {
        return (
          <>
            <Title text="You have unlocked FREE Play!" />
            <PromoIcon src={FreePlayIcon} alt="" className="free-play-large" />
            <Link className="outlined-btn" to={WSTORE_LINK_TO}>
              Redeem
            </Link>
            <SupportingText>
              You have <strong> {totalSapphiresEarned}</strong> Sapphires to
              spend at the WStore!
              <ActionButton
                text="Visit WStore"
                to={WSTORE_LINK_TO}
                className="visit-wstore-btn"
              />
            </SupportingText>
          </>
        );
      } else if (usersFreePlayRewardValue) {
        return (
          <>
            <Title text="Your rewards are calling!" />
            <PromoIcon src={propertyIcon} alt={casinoLocation} />
            <SupportingText className="location-based">
              You have <strong>{usersFreePlayRewardValue} in FREE Play!</strong>
              <br />
              Use your rewards at {casinoLocation} before they expire. Come
              visit us!
            </SupportingText>
            <ActionButton text="Plan Your Visit" onClick={openBookingPage} />
          </>
        );
      }
    }
  };

  return <div className="promo-section-container">{renderContent()}</div>;
};

PromoSection.propTypes = {
  remainingSapphiresToEarn: PropTypes.number,
  totalSapphiresToEarn: PropTypes.number,
  casinoLocation: PropTypes.string,
  patronRewards: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      description: PropTypes.string
    })
  ),
  plsRewards: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      remainingValue: PropTypes.string
    })
  )
};

export default PromoSection;
